// GlobalScanFetcher.jsx
import { useEffect, useMemo } from 'react';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import useLatestScanTime from '@root/hooks/useLatestScanTime';
import agent from '@root/agent';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEnvironments, useSelectedEnvironments } from '@root/hooks/environments';
import { useCloudProvider } from '../v2/content/CloudProviderBar/CloudProviderContext';
const TIMEZONE = dayjs.tz.guess();

dayjs.extend(utc);
dayjs.extend(timezone);
const GlobalScanFetcher = () => {
  const { setLatestScanTime, setLastestScanId, setLatestK8sScanTime } = useLatestScanTime();
  const { selectedEnvironments } = useSelectedEnvironments();
  const pathname = location.pathname;
  const { cloudProvider } = useCloudProvider();
  const allEnvironments = useEnvironments();
  const environments = useMemo(() => {
    if (selectedEnvironments.length && cloudProvider !== 'all') {
      return selectedEnvironments.filter(environment => environment.cloudProvider === cloudProvider);
    } else if (cloudProvider === 'all') {
      return selectedEnvironments.filter(
        environment =>
          environment.cloudProvider === 'aws' ||
          environment.cloudProvider === 'gcp' ||
          environment.cloudProvider === 'azure',
      );
    }
    return allEnvironments;
  }, [selectedEnvironments, allEnvironments, cloudProvider]);

  const { data: latestScan, isFetching: isFetchingLatestScan } = useEnhancedQuery(
    ['get_latest_scan_data', environments?.map(e => e.id), location.pathname],
    () =>
      agent.Cspa.get_latest_scan_data({
        environments: environments.map(e => e.id),
        timezone: TIMEZONE,
      }),
    {
      enabled: environments?.length > 0,
      staleTime: 0, // Always refetch
      refetchOnMount: 'always', // ⭐ Refetch per page
      refetchOnWindowFocus: false,
    },
  );
  const isK8Route =
    pathname.startsWith('/kubernetes-live') || pathname.startsWith('/k8') || pathname.includes('/kubernetes');
  const { data: latestK8sScan, isFetching: isFetchingLatestK8sScan } = useEnhancedQuery(
    ['get_latest_scan k8__data', environments.map(item => item.id), location.pathname],
    () =>
      agent.Kubernetes.get_k8_last_scan({
        environments: environments.map(e => e.id),
        // timezone: TIMEZONE, // cloud_provider: cloudProvider,
      }),
    {
      enabled: environments.length > 0 && isK8Route,
      staleTime: 0,
      refetchOnMount: 'always', // ⭐ Refetch per page
      refetchOnWindowFocus: false,
    },
  );
  useEffect(() => {
    if (!isFetchingLatestK8sScan && latestK8sScan) {
      setLatestK8sScanTime(latestK8sScan?.data);
    }
  }, [isFetchingLatestK8sScan, latestK8sScan, setLatestK8sScanTime]);

  useEffect(() => {
    if (!isFetchingLatestScan && latestScan) {
      setLatestScanTime(latestScan);
    }
  }, [isFetchingLatestScan, latestScan, setLastestScanId, setLatestScanTime]);
  useEffect(() => {
    if (latestScan) {
      setLatestScanTime(latestScan);
    }
  }, [latestScan, setLatestScanTime]);

  return null;
};

export default GlobalScanFetcher;
